import { Component } from '@verndale/core';
import { LoadSequenceBasic } from '../../helpers/animations';
import { mediaQuery } from '../../helpers/media-queries';

class Search extends Component {
  searchOpen = false;
  qs = mediaQuery('desktop');
  endpoint = this.el.dataset.suggestionsEndpoint;
  searchUrl = this.el.dataset.searchUrl;
  language = this.el.dataset.language;

  setupDefaults() {
    this.searchOpen = false;

    this.dom = {
      $topSection: this.el.querySelector<HTMLElement>('.search__top'),
      $bottomSection: this.el.querySelector<HTMLElement>('.search__bottom'),
      $searchField: this.el.querySelector<HTMLInputElement>('.form-input__control'),
      $resetBtn: this.el.querySelector<HTMLButtonElement>('.form-input__cancel-btn'),
      $featured: this.el.querySelector<HTMLElement>('.search__featured'),
      $resources: this.el.querySelector<HTMLElement>('.search__resources'),
      $suggested: this.el.querySelector<HTMLElement>('.search__suggested'),
      $searchBtn: this.el.querySelector<HTMLButtonElement>('.button--search-btn')
    };
    this.checkUrlParams();
  }

  addListeners() {
    if (!this.el.closest('.search--mobile')) { // search is always open on mobile nav
      window.addEventListener('DESKTOP_SEARCH_OPEN', this.openSearch.bind(this));
      window.addEventListener('DESKTOP_SEARCH_CLOSE', this.closeSearch.bind(this));
    }

    document.addEventListener('click', this.handleSearchElementClick.bind(this));
    (this.dom.$resetBtn as HTMLElement).addEventListener('click', this.handleResetBtnClick.bind(this));
    (this.dom.$searchField as HTMLElement).addEventListener('keyup', this.handleSearchFieldChange.bind(this));
    (this.dom.$searchField as HTMLElement).addEventListener('focusin', this.handleResourcesAndFeaturedLists.bind(this));
    (this.dom.$searchBtn as HTMLElement)?.addEventListener('click', this.handleSearchBtnClick.bind(this));
    document.addEventListener('keydown', this.handleEnterKey.bind(this));
    document.addEventListener('keydown', this.handleEscKey.bind(this));
  }

  handleEnterKey(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      if (this.el.contains(e.target as Node) && (this.dom.$searchField as HTMLInputElement).value) {
        this.handleSearchBtnClick();
      }
    }
  }

  handleEscKey(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      if (this.el.contains(e.target as Node) && (this.dom.$searchField as HTMLInputElement).value) {
        this.handleResetBtnClick();
      }
    }
  }

  handleSearchElementClick(e: Event) {
    if (!this.qs.matches) {
      if ((e.target as HTMLElement).classList.contains('link__text') || (e.target as HTMLElement).classList.contains('form-input__control')) {
        return;
      }
      this.hideResourcesAndFeaturedLists();
    }
  }

  checkUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const searchValue = urlParams.get('q');
    const searchField = this.dom.$searchField as HTMLInputElement;

    if (searchValue) {
      searchField.value = searchValue;
    }
  }

  handleResourcesAndFeaturedLists() {
    if (!this.qs.matches) {
      if (this.dom.$featured && (this.dom.$searchField as HTMLInputElement)?.value.length === 0) {
        (this.dom.$featured as HTMLElement).classList.remove('hidden');
      }

      if (this.dom.$resources && (this.dom.$searchField as HTMLInputElement)?.value.length === 0) {
        (this.dom.$resources as HTMLElement).classList.remove('hidden');
      }

      if (this.dom.$suggested && (this.dom.$searchField as HTMLInputElement)?.value.length !== 0) {
        (this.dom.$suggested as HTMLElement).classList.remove('hidden');
      }

      (this.el as HTMLElement).classList.add('search--active');
    }
  }

  hideResourcesAndFeaturedLists() {
    if (!this.qs.matches) {
      (this.dom.$featured as HTMLElement)?.classList.add('hidden');
      (this.dom.$resources as HTMLElement)?.classList.add('hidden');
      (this.dom.$suggested as HTMLElement)?.classList.add('hidden');
      (this.el as HTMLElement)?.classList.remove('search--active');
    }
  }

  handleResetBtnClick() {
    const searchField = this.dom.$searchField as HTMLInputElement;
    const suggestionsContainer = this.el.querySelector('.search__suggested .search__featured-list') as HTMLElement;
    const featured = this.dom.$featured as HTMLElement;
    const resources = this.dom.$resources as HTMLElement;
    const suggested = this.dom.$suggested as HTMLElement;

    suggestionsContainer.innerHTML = '';
    searchField.value = '';

    featured?.classList.remove('hidden');
    resources?.classList.remove('hidden');
    suggested?.classList.add('hidden');
    (this.el as HTMLElement).classList.remove('search--active');
  }

  handleSearchBtnClick() {
    (this.el as HTMLElement).classList.remove('search--active');
    const searchField = this.dom.$searchField as HTMLInputElement;
    const searchValue = searchField.value;
    const searchUrl = `${this.searchUrl}?q=${searchValue}`;

    window.location.href = searchUrl;
  }

  handleSearchFieldChange() {
    const searchField = this.dom.$searchField as HTMLInputElement;
    const featured = this.dom.$featured as HTMLElement;
    const resources = this.dom.$resources as HTMLElement;
    const suggested = this.dom.$suggested as HTMLElement;
    const searchValue = searchField.value;

    if (searchValue.length > 0) {
      fetch(`${this.endpoint}?q=${searchValue}&language=${this.language}`)
        .then(response => response.json())
        .then(data => {
          const suggestions = data.data;
          const suggestionsContainer = this.el.querySelector('.search__suggested .search__featured-list') as HTMLElement;
          let suggestionsHTML = '';

          if (suggestions.length > 0) {
            suggestions.forEach((suggestion: { term: string }) => {
              const boldedSuggestion = suggestion.term.replace(new RegExp(searchValue, 'gi'), (match) => `<strong>${match}</strong>`);
              const suggestionHTML = `<li class="search__featured-item">
                <a class="search__featured-item-link" href="${this.searchUrl}?q=${suggestion.term}">${boldedSuggestion}</a>
              </li>`;
              suggestionsHTML += suggestionHTML;
            });

            featured?.classList.add('hidden');
            resources?.classList.add('hidden');
            suggested?.classList.remove('hidden');
          } else {
            featured?.classList.remove('hidden');
            resources?.classList.remove('hidden');
            suggested?.classList.add('hidden');
          }

          if (suggestionsContainer) {
            suggestionsContainer.innerHTML = suggestionsHTML;
          }
        });
    } else {
      featured?.classList.remove('hidden');
      resources?.classList.remove('hidden');
      suggested?.classList.add('hidden');
    }
  }

  openSearch() {
    if (this.searchOpen) return;

    const elements: { element: HTMLElement, addScale: boolean, offset?: string | number }[] = [];

    this.searchOpen = true;
    this.el.classList.remove('search--hidden');
    this.el.setAttribute('aria-hidden', 'false');

    elements.push({ element: this.el, addScale: false, offset: 0 });
    elements.push({ element: (this.dom.$topSection as HTMLElement), addScale: false, offset: 0.2 });
    elements.push({ element: (this.dom.$bottomSection as HTMLElement), addScale: false, offset: 0.3 });

    LoadSequenceBasic(elements);

    (this.dom.$searchField as HTMLInputElement).focus();
  }

  closeSearch() {
    if (!this.searchOpen) return;

    this.searchOpen = false;
    this.el.classList.add('search--hidden');
    this.el.setAttribute('aria-hidden', 'true');

    window.dispatchEvent(new CustomEvent('DESKTOP_SEARCH_CLOSE', { bubbles: true }));
  }
}

export default Search;
